import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

// Declare global variable from Vite config
declare const __APP_VERSION__: string;

// Get environment from Vite environment variables
const environment = import.meta.env.MODE || 'development';
const isDevelopment = environment === 'development';

// Initialize Sentry only if DSN is provided
const dsn = import.meta.env.VITE_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    // Set the environment
    environment,
    // Set the release version for source map tracking (CalVer format: yyyy.mm.dd.hhmm)
    release: `app@${__APP_VERSION__}`,
    // Send default PII data (IP addresses, user info)
    sendDefaultPii: true,

    integrations: [
      // Browser tracing integration for performance monitoring
      Sentry.browserTracingIntegration(),
      // Session replay integration for debugging
      Sentry.replayIntegration({
        // Mask all text and input content for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
      // React Router integration for better transaction names
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation: useLocation,
        useNavigationType: useNavigationType,
        createRoutesFromChildren: createRoutesFromChildren,
        matchRoutes: matchRoutes,
      }),
    ],

    // Tracing - Performance monitoring
    // Development: 0% to avoid cluttering Sentry, Production: 10-20% for cost efficiency
    tracesSampleRate: isDevelopment
      ? 0.0
      : parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),

    // Session Replay - Records user sessions for debugging
    // Development: 0% to avoid cluttering Sentry, Production: 10% for cost efficiency
    replaysSessionSampleRate: isDevelopment
      ? 0.0
      : parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'),

    // Capture 100% of sessions with errors (regardless of environment)
    replaysOnErrorSampleRate: 1.0,

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Ignore specific errors that are expected or not actionable
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors that are transient
      'NetworkError',
      'Failed to fetch',
      // ResizeObserver errors (not actionable)
      'ResizeObserver loop limit exceeded',
    ],

    // Additional configuration for better error grouping
    beforeSend(event) {
      // Filter out development errors if needed
      if (isDevelopment && event.message?.includes('HMR')) {
        return null;
      }

      return event;
    },
  });

  // Log that Sentry is initialized
  console.info(`Sentry initialized in ${environment} mode`);
} else {
  console.warn('Sentry DSN not configured - error tracking disabled');
}
